import { CandidateRow } from "@components/campaigns/stages/CandidateRow";
import { RosterStatusBadge } from "@components/campaigns/stages/RosterStatusBadge";
import {
  StageDropdown,
  TerminalStageMoveControl,
} from "@components/campaigns/stages/StageDropdown";
import { useReturnToPool } from "@components/campaigns/useReturnToPool";
import { AddCandidateToListDialog } from "@components/candidates/AddCandidateToListDialog";
import { QueryErrorState } from "@components/shared/QueryErrorState";
import { MOVE_CANDIDATE_STAGE, PROMOTE_POOL_MEMBERSHIP } from "@lib/graphql/mutations";
import { POOL_ROSTER_QUERY } from "@lib/graphql/queries";
import { useCampaignChannel } from "@lib/hooks/useCampaignChannel";
import { type Connection, usePaginatedList } from "@lib/hooks/usePaginatedList";
import { usePersistedFlag } from "@lib/hooks/usePersistedFlag";
import {
  useCampaignSelection,
  useCandidateSelectionStore,
} from "@lib/stores/candidateSelectionStore";
import { TERMINAL_STAGES } from "@lib/types";
import { ArrowDownAZ, ArrowRight, ArrowUpAZ, Plus, Users } from "lucide-react";
import { useCallback, useState } from "react";
import { useMutation } from "urql";
import { useCampaignDetail } from "./context";

/** localStorage key for the "Not yet progressed only" toggle — recruiters flip it once. */
export const NOT_YET_PROGRESSED_STORAGE_KEY = "talent-pool-roster-not-yet-progressed";

type RosterSortBy = "STATUS" | "NAME" | "LAST_INTERACTION" | "ADDED";
type SortDirection = "ASC" | "DESC";

const SORT_OPTIONS: { value: RosterSortBy; label: string }[] = [
  { value: "STATUS", label: "Status" },
  { value: "NAME", label: "Name" },
  { value: "LAST_INTERACTION", label: "Last activity" },
  { value: "ADDED", label: "Date added" },
];

interface RosterCandidate {
  id: string;
  referenceNumber: number | null;
  fullName: string;
  email: string | null;
  currentRoleTitle: string | null;
  currentCompany: string | null;
  location: string | null;
  linkedinUrl: string | null;
  resumes?: { id: string; filename: string; url: string; byteSize: number; createdAt: string }[];
}

export interface RosterRow {
  id: string;
  kind: "pool" | "pipeline";
  status: string;
  interacted: boolean;
  lastInteractionAt: string;
  reason: string | null;
  returnedFromStage: string | null;
  returnedAt: string | null;
  source: string | null;
  notes: string | null;
  createdAt: string;
  candidate: RosterCandidate;
  addedBy: { id: string; fullName: string };
}

export interface RosterSummary {
  total: number;
  notYetProgressed: number;
  returned: number;
  inPipeline: number;
  placed: number;
  rejected: number;
  withdrawn: number;
}

interface PoolRosterData {
  campaign: {
    id: string;
    pool: { id: string } | null;
    poolRosterSummary: RosterSummary;
    poolRoster: Connection<RosterRow> & { totalCount: number };
  } | null;
}

export function PoolTab() {
  const { campaign } = useCampaignDetail();
  const campaignId = campaign.id;
  const [notYetProgressedOnly, setNotYetProgressedOnly] = usePersistedFlag(
    NOT_YET_PROGRESSED_STORAGE_KEY,
    false,
  );
  const [sortBy, setSortBy] = useState<RosterSortBy>("STATUS");
  const [sortDirection, setSortDirection] = useState<SortDirection>("ASC");
  const [adding, setAdding] = useState(false);

  const {
    nodes: rows,
    data,
    fetching,
    error,
    hasNextPage,
    loadMore,
    refresh,
  } = usePaginatedList<PoolRosterData, RosterRow>({
    query: POOL_ROSTER_QUERY,
    variables: {
      id: campaignId,
      interacted: notYetProgressedOnly ? false : null,
      sortBy,
      sortDirection,
    },
    getConnection: (d) => d.campaign?.poolRoster,
  });
  useCampaignChannel(campaignId, refresh);

  const [, promoteMembership] = useMutation(PROMOTE_POOL_MEMBERSHIP);
  const [, moveStage] = useMutation(MOVE_CANDIDATE_STAGE);
  const { openFor: openReturnToPool, dialog: returnToPoolDialog } = useReturnToPool(refresh);

  const selectedIds = useCampaignSelection(campaignId);
  const toggleSelection = useCandidateSelectionStore((s) => s.toggle);
  const setManySelected = useCandidateSelectionStore((s) => s.setMany);
  const clearSelection = useCandidateSelectionStore((s) => s.clear);
  const selectedSet = new Set(selectedIds);

  const talentPoolId: string | undefined = data?.campaign?.pool?.id;
  const summary: RosterSummary | undefined = data?.campaign?.poolRosterSummary;
  const totalCount = data?.campaign?.poolRoster?.totalCount ?? rows.length;

  // Only pool rows can be bulk-promoted; the selection store is a plain id
  // set, so eligibility is decided here against the rows on screen.
  const selectablePoolIds = rows.filter((row) => row.kind === "pool").map((row) => row.id);

  const handlePromote = async (membershipId: string) => {
    await promoteMembership({ input: { talentPoolMembershipId: membershipId } });
    refresh();
  };

  const handleBulkPromote = async () => {
    const ids = selectedIds.filter((id) => selectablePoolIds.includes(id));
    if (ids.length === 0) return;
    await Promise.all(
      ids.map((id) => promoteMembership({ input: { talentPoolMembershipId: id } })),
    );
    clearSelection(campaignId);
    refresh();
  };

  const handleMove = async (campaignCandidateId: string, stage: string) => {
    await moveStage({ input: { campaignCandidateId, stage } });
    refresh();
  };

  if (fetching && !data) {
    return <p className="text-sm text-text-muted">Loading candidates…</p>;
  }

  const empty = rows.length === 0;
  const everyoneProgressed = notYetProgressedOnly && (summary?.total ?? 0) > 0;

  return (
    <div className="space-y-4">
      <div className="rounded-lg bg-card p-4 shadow-sm ring-1 ring-ring">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-y-2">
          <div className="flex items-center gap-2">
            <Users size={16} className="text-text-faint" />
            <h3 className="text-sm font-medium text-text-secondary">Pool</h3>
            {summary && <span className="text-xs text-text-faint">{summaryLine(summary)}</span>}
          </div>
          <div className="flex items-center gap-3">
            {!adding && (
              <button
                type="button"
                onClick={() => setAdding(true)}
                className="flex items-center gap-1 rounded px-2 py-1 text-xs text-accent hover:bg-accent/5"
              >
                <Plus size={12} /> Add Candidate
              </button>
            )}
          </div>
        </div>

        <div className="mb-3 flex flex-wrap items-center justify-between gap-3 text-xs text-text-muted">
          <label
            className="flex cursor-pointer items-center gap-2"
            title="Hides anyone who has ever been on the long list or beyond, including rejected and returned."
          >
            <input
              type="checkbox"
              checked={notYetProgressedOnly}
              onChange={(e) => setNotYetProgressedOnly(e.target.checked)}
              className="h-4 w-4 cursor-pointer rounded border-border accent-accent"
            />
            <span>
              Not yet progressed only
              <span className="ml-1 text-text-faint">
                (hides anyone who has been on the long list or beyond, including rejected and
                returned)
              </span>
            </span>
          </label>
          <div className="flex items-center gap-1">
            <label htmlFor="pool-roster-sort">Sort by</label>
            <select
              id="pool-roster-sort"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as RosterSortBy)}
              className="rounded-md border border-input-border bg-card px-2 py-1 text-xs text-text-primary focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
            >
              {SORT_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <button
              type="button"
              onClick={() => setSortDirection((d) => (d === "ASC" ? "DESC" : "ASC"))}
              aria-label={sortDirection === "ASC" ? "Sort ascending" : "Sort descending"}
              title={
                sortDirection === "ASC"
                  ? "Ascending — click for descending"
                  : "Descending — click for ascending"
              }
              className="rounded p-1 text-text-faint hover:bg-card-hover hover:text-text-secondary"
            >
              {sortDirection === "ASC" ? <ArrowDownAZ size={14} /> : <ArrowUpAZ size={14} />}
            </button>
          </div>
        </div>

        {adding && talentPoolId && (
          <div className="mb-4">
            <AddCandidateToListDialog
              target={{ kind: "talent_pool", talentPoolId }}
              heading="Add Candidate to Pool"
              onDone={() => {
                setAdding(false);
                refresh();
              }}
              onCancel={() => setAdding(false)}
            />
          </div>
        )}

        {error ? (
          <QueryErrorState error={error} what="the pool" onRetry={refresh} retrying={fetching} />
        ) : empty && !adding ? (
          <p className="py-6 text-center text-sm text-text-faint">
            {everyoneProgressed
              ? 'Everyone in this campaign has already been progressed. Turn off "Not yet progressed only" to see them.'
              : "No candidates yet. Add candidates here before promoting them to the long list."}
          </p>
        ) : (
          <div>
            {selectablePoolIds.length > 0 && (
              <PoolSelectionControls
                selectablePoolIds={selectablePoolIds}
                selectedIds={selectedIds}
                onToggleAll={(selectAll) =>
                  setManySelected(campaignId, selectablePoolIds, selectAll)
                }
                onClear={() => clearSelection(campaignId)}
                onPromote={handleBulkPromote}
              />
            )}
            {rows.map((row) => (
              <CandidateRow
                key={row.id}
                candidate={row.candidate}
                source={row.source}
                addedBy={{ name: row.addedBy.fullName }}
                createdAt={row.createdAt}
                statusBadge={
                  <RosterStatusBadge
                    status={row.status}
                    returnedFromStage={row.returnedFromStage}
                    returnedAt={row.returnedAt}
                    reason={row.reason}
                  />
                }
                selected={row.kind === "pool" ? selectedSet.has(row.id) : undefined}
                onToggleSelect={
                  row.kind === "pool" ? () => toggleSelection(campaignId, row.id) : undefined
                }
                campaignId={campaignId}
                campaignCandidateId={row.kind === "pipeline" ? row.id : undefined}
                actions={rowActions(row, {
                  onPromote: handlePromote,
                  onMove: handleMove,
                  onReturnToPool: (r) =>
                    openReturnToPool({ id: r.id, candidate: r.candidate }, r.status),
                })}
              />
            ))}
            {hasNextPage && (
              <div className="mt-4 flex flex-col items-center gap-1">
                <span className="text-xs text-text-faint">
                  Showing {rows.length} of {totalCount}
                </span>
                <button
                  type="button"
                  onClick={loadMore}
                  disabled={fetching}
                  className="rounded-md border border-input-border bg-card px-4 py-2 text-sm font-medium text-text-secondary hover:bg-card-hover disabled:opacity-50"
                >
                  {fetching ? "Loading..." : "Load more"}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
      {returnToPoolDialog}
    </div>
  );
}

/** "42 candidates · 30 not yet progressed · 8 in pipeline · 4 rejected" — zero buckets are omitted. */
export function summaryLine(summary: RosterSummary): string {
  const parts = [`${summary.total} ${summary.total === 1 ? "candidate" : "candidates"}`];
  const buckets: [number, string][] = [
    [summary.notYetProgressed, "not yet progressed"],
    [summary.returned, "returned to pool"],
    [summary.inPipeline, "in pipeline"],
    [summary.placed, "placed"],
    [summary.rejected, "rejected"],
    [summary.withdrawn, "withdrawn"],
  ];
  for (const [count, label] of buckets) {
    if (count > 0) parts.push(`${count} ${label}`);
  }
  return parts.join(" · ");
}

interface RowActionHandlers {
  onPromote: (membershipId: string) => void;
  onMove: (campaignCandidateId: string, stage: string) => void;
  onReturnToPool: (row: RosterRow) => void;
}

/** Stage-appropriate action per row: promote (pool), move / return to pool (pipeline), move (terminal). */
function rowActions(row: RosterRow, handlers: RowActionHandlers) {
  if (row.kind === "pool") {
    return (
      <button
        type="button"
        onClick={() => handlers.onPromote(row.id)}
        className="flex items-center gap-1 rounded px-2 py-1 text-xs text-accent hover:bg-accent/5"
        title="Add to long list"
      >
        <ArrowRight size={12} /> Add to long list
      </button>
    );
  }
  if ((TERMINAL_STAGES as readonly string[]).includes(row.status)) {
    return (
      <TerminalStageMoveControl
        stage={row.status}
        onMove={(stage) => handlers.onMove(row.id, stage)}
      />
    );
  }
  return (
    <StageDropdown
      currentStage={row.status}
      onMove={(stage) => handlers.onMove(row.id, stage)}
      onReturnToPool={() => handlers.onReturnToPool(row)}
    />
  );
}

interface PoolSelectionControlsProps {
  selectablePoolIds: string[];
  selectedIds: string[];
  onToggleAll: (selectAll: boolean) => void;
  onClear: () => void;
  onPromote: () => void;
}

function PoolSelectionControls({
  selectablePoolIds,
  selectedIds,
  onToggleAll,
  onClear,
  onPromote,
}: PoolSelectionControlsProps) {
  const selectedCount = selectedIds.filter((id) => selectablePoolIds.includes(id)).length;
  const allSelected = selectablePoolIds.length > 0 && selectedCount === selectablePoolIds.length;
  const someSelected = selectedCount > 0 && !allSelected;

  const headerCheckboxRef = useCallback(
    (node: HTMLInputElement | null) => {
      if (node) node.indeterminate = someSelected;
    },
    [someSelected],
  );

  return (
    <div className="mb-2 flex items-center justify-between gap-3 border-b border-border px-3 py-2">
      <label className="flex items-center gap-2 text-xs text-text-muted">
        <input
          ref={headerCheckboxRef}
          type="checkbox"
          aria-label="Select all pool candidates"
          checked={allSelected}
          onChange={(e) => onToggleAll(e.target.checked)}
          className="h-4 w-4 cursor-pointer rounded border-border accent-accent"
        />
        <span>{selectedCount === 0 ? "Select pool candidates" : `${selectedCount} selected`}</span>
      </label>
      {selectedCount > 0 && (
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onClear}
            className="rounded px-2 py-1 text-xs text-text-muted hover:bg-card-hover hover:text-text-secondary"
          >
            Clear
          </button>
          <button
            type="button"
            onClick={onPromote}
            className="flex items-center gap-1 rounded bg-accent px-3 py-1 text-xs font-medium text-white hover:bg-accent/90"
          >
            <ArrowRight size={12} /> Add to long list
          </button>
        </div>
      )}
    </div>
  );
}
